import React from "react";
import {
  View,
  Text,
  StyleSheet,
  Svg,
  Rect,
  Defs,
  LinearGradient,
  Stop,
  Path,
} from "@react-pdf/renderer";

// QuestionChart Component
const QuestionChart = ({ question, prevQuestion }) => {
  const norm = question.normAverage
    ? (question.normAverage * 10).toFixed(0)
    : null;
  const barStartX = 30; // The X position where the bar starts
  const barWidth = 350; // Total width of the gradient bar

  // Ensure norm is correctly converted from 1-10 scale to 0-100 scale
  const normPercentage = (question.normAverage / 10) * 100;

  // Use the correct proportion of the bar width
  const normX = 70 + (normPercentage / 100) * barWidth + 7; // Adjust by 5px if needed  // {
  //   dimensionTitle: "Performance",
  //   factorTitle: "Clarity",
  //   questionIndex: 0,
  //   questionText: "Role responsibilities are well defined.",
  //   distribution: [...],   // overall distribution
  //   overallAverage: 6.85,
  //   overallStdev: 2.12,
  //   normAverage: 7.5,
  //   roles: [
  //     {
  //       roleId: "...",
  //       roleName: "innerlogic1",
  //       color: "#ce7118",
  //       average: 6.85,
  //       distribution: [...],
  //       stdev: 2.12,
  //       n: 272
  //     },
  //     ...
  //   ]
  // }

  // Convert each role into a "bar" row in the chart.
  // We'll treat role.average as a 1–10 scale,
  // multiply by 10 => 0–100 to get a percentage for the bar.
  const questionAnalysis = question.roles.map((role, i) => {
    const percentage = Math.round(role.average * 10); // e.g. 6.85 => 68.5 => 69
    const prevAvg = prevQuestion
      ? prevQuestion?.roles?.find((r) => r?.roleId === role?.roleId)?.average
      : 0;
    const change = prevQuestion ? percentage - Math.round(prevAvg * 10) : null;

    return {
      group: role.roleName,
      score: percentage,
      change,
      color: role.color,
    };
  });

  const truncateText = (text, maxLength) => {
    return text.length > maxLength ? text.slice(0, maxLength) + "..." : text;
  };

  const GroupLabel = ({ label }) => {
    const segments = label.split("/");
    return (
      <View style={styles.groupContainer}>
        {segments.map((segment, index) => (
          <Text
            key={index}
            style={[
              styles.groupLine,
              // Optionally add extra spacing to all but the last line:
              index < segments.length - 1 && { marginBottom: 1 },
            ]}
          >
            {index === 0 ? segment.trim() : "/" + segment.trim()}
          </Text>
        ))}
      </View>
    );
  };

  return (
    <View style={styles.container} wrap={false}>
      <Text style={styles.title}>{question.factorTitle} Question</Text>

      <Text style={styles.question}>
        {`${question.questionIndex + 1}. ${question.questionText}`}
      </Text>

      {/* Divider */}
      <View style={styles.divider} />

      <View style={styles.chartContainer}>
        {/* Render each role as a bar */}
        {questionAnalysis.map((analysis, index) => (
          <View key={index} style={styles.analysisRow}>
            {/* Group Name */}
            {/* Group Name – force one line with ellipsis if too long */}
            <GroupLabel label={analysis.group} />
            <View style={styles.progressBarWrapper}>
              <View style={[styles.filledBar, { width: `${analysis.score}%` }]}>
                <GradientBar width={analysis.score} />
              </View>
            </View>

            {/* Score */}
            <View style={styles.scoreRow}>
              <Text style={styles.score}>{`${analysis.score}%`}</Text>
              {(!!analysis?.change && (
                <Text
                  style={[
                    styles.score,
                    styles.changeColumn,
                    analysis?.change < 0
                      ? styles.negativeChange
                      : analysis?.change === 0
                      ? styles.neutralChange
                      : styles.positiveChange,
                    {
                      marginLeft: 10,
                    },
                  ]}
                >
                  ({analysis?.change}%)
                </Text>
              )) || (
                <View
                  style={{
                    marginLeft: 10,
                  }}
                />
              )}
            </View>
          </View>
        ))}
        {/* Dotted norm line overlay */}
        {norm !== null && (
          <View
            style={{
              position: "absolute",
              top: 0,
              bottom: 0,
              left: normX,
              width: 1,
              borderLeftWidth: 1,
              borderLeftColor: "#000",
              borderStyle: "dotted",
            }}
          />
        )}
      </View>
      <View style={styles.legendContainer}>
        <View style={styles.legendItem}>
          <View style={styles.legendLine} />
          <Text style={styles.legendText}>Global Norm</Text>
        </View>
      </View>
    </View>
  );
};

/* --- The rest of your existing chart code unchanged, except we use each role's color or a dynamic gradient if you want. --- */

// Keep your existing gradient logic or adapt to the role color
const getGradientColorAtPosition = (percentage) => {
  if (percentage <= 45) {
    const ratio = percentage / 45;
    return interpolateColor("#F8A8B0", "#FECD8D", ratio);
  } else if (percentage <= 75) {
    const ratio = (percentage - 45) / 30;
    return interpolateColor("#FECD8D", "#95E6D2", ratio);
  } else {
    return "#95E6D2";
  }
};

const interpolateColor = (color1, color2, ratio) => {
  const hexToRgb = (hex) => {
    const bigint = parseInt(hex.replace("#", ""), 16);
    return [(bigint >> 16) & 255, (bigint >> 8) & 255, bigint & 255];
  };

  const [r1, g1, b1] = hexToRgb(color1);
  const [r2, g2, b2] = hexToRgb(color2);

  const r = Math.round(r1 + ratio * (r2 - r1));
  const g = Math.round(g1 + ratio * (g2 - g1));
  const b = Math.round(b1 + ratio * (b2 - b1));

  return `rgb(${r}, ${g}, ${b})`;
};

const SvgHalfMoonCap = ({ width }) => {
  const fillColor = getGradientColorAtPosition(width);
  const barWidth = 350;

  const left = (width / 100) * barWidth;

  return (
    <Svg
      width={20}
      height={10.3}
      viewBox="0 0 24 24"
      style={{
        position: "absolute",
        left: left - 10,
        top: 0,
      }}
    >
      <Path
        d="M12 0a12 12 0 0112 12 12 12 0 01-12 12V0z"
        fill={fillColor}
        fillOpacity={0.5}
      />
    </Svg>
  );
};

const GradientBar = ({ width }) => (
  <Svg
    height={10}
    width={350}
    style={{
      flex: 1,
      width: "100%",
      opacity: 0.5,
    }}
    preserveAspectRatio="none"
  >
    <Defs>
      <LinearGradient id="opacityGradient" x1="0" y1="0" x2="1" y2="1">
        <Stop offset="0%" stopColor="#F8A8B0" />
        <Stop offset="45%" stopColor="#FECD8D" />
        <Stop offset="75%" stopColor="#95E6D2" />
        <Stop offset="100%" stopColor="#95E6D2" />
      </LinearGradient>
    </Defs>
    {/* Full gradient rect */}
    <Rect
      x="0"
      y="1"
      width="99%"
      height="95%"
      fill="url(#opacityGradient)"
      fillOpacity={0.5}
    />
    {/* White overlay from `width`% to the end, so the gradient appears only for the "filled" portion */}
    <Rect
      x={`${width + 2}%`}
      y="0"
      width={`${105 - width}%`}
      height={20}
      rx={0.5}
      ry={-0.5}
      fill="#FFFFFF"
      style={{
        position: "absolute",
        left: (width / 100) * 350 - 10,
        top: -1.02,
        height: 10.3,
      }}
    />
  </Svg>
);

const styles = StyleSheet.create({
  container: {
    padding: 20,
    borderRadius: 14,
    backgroundColor: "#FFFFFF",
    marginBottom: 20,
    borderColor: "#EBF1FD",
    borderWidth: 1,
  },
  title: {
    fontSize: 10,
    color: "#2D70E2",
    marginBottom: 12,
    fontFamily: "Raleway",
    fontWeight: 600,
  },
  question: {
    fontSize: 12,
    color: "#2A3039",
    marginBottom: 12,
    fontFamily: "Raleway",
    fontWeight: 600,
  },
  divider: {
    height: 1,
    backgroundColor: "#E0E0E0",
    marginVertical: 10,
  },
  analysisRow: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: -4,
    width: "100%",
  },
  chartContainer: {
    position: "relative",
  },
  scoreRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    minWidth: 50,
    maxWidth: 50,
  },
  group: {
    fontSize: 6,
    color: "#666D79",
    fontFamily: "Raleway",
    fontWeight: 600,
    maxWidth: 40,
    minWidth: 40,
  },
  progressBarWrapper: {
    width: "100%",
    height: 26,
    overflow: "hidden",
    position: "relative",
    flexDirection: "row",
    left: 30,
    top: 8,
  },
  filledBar: {
    position: "relative",
    height: "100%",
  },
  score: {
    fontSize: 10,
    color: "#666D79",
    fontFamily: "Raleway",
    fontWeight: 600,
  },
  questionIconWrapper: {
    marginBottom: 10,
  },
  positiveChange: {
    color: "#1D9A7D", // Secondary/600
  },
  negativeChange: {
    color: "#E34D59", // Example negative color
  },
  neutralChange: {
    color: "#F5A623", // Example neutral color
  },
  legendContainer: {
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    marginTop: 10,
  },
  legendItem: {
    flexDirection: "row",
    alignItems: "center",
  },
  legendLine: {
    width: 20,
    borderBottomWidth: 1,
    borderBottomColor: "#000",
    borderStyle: "dotted",
    marginRight: 5,
  },
  legendText: {
    fontSize: 10,
    color: "#666D79",
    fontFamily: "Raleway",
    fontWeight: 600,
  },
  groupContainer: {
    minHeight: 30, // Set a consistent minimum height
    maxWidth: 40,
    minWidth: 40,
    justifyContent: "center",
  },
  groupLine: {
    fontSize: 6,
    color: "#666D79",
    fontFamily: "Raleway",
    fontWeight: 600,
  },
});

export default QuestionChart;